"use client"

import { useEffect, useTransition } from "react"
import { isDevTenantOverrideAllowed } from "@/lib/tenant/devTenantOverride"
import { TENANTS, type Tenant } from "@/lib/tenant/tenant"
import { setDevTenantOverride } from "@/lib/tenant/setDevTenantOverride"

type Props = {
  current: Tenant
}

// Dev-only banner control. Cycles through the known tenants and writes the
// cookie via the server action; the action revalidates so the next render
// reflects the override across the app.
export const DevTenantToggle = ({ current }: Props) => {
  const [isPending, startTransition] = useTransition()
  const currentIndex = TENANTS.indexOf(current)
  const next = TENANTS[(currentIndex + 1) % TENANTS.length]

  // Keeps the CSS tenant theming ([data-tenant] on <html>, set at build time
  // by the root layout) in sync with the dev-override cookie. EnvBanner only
  // renders this component when the override is allowed, but the guard makes
  // the mutation locally unreachable in production builds regardless of the
  // caller (NODE_ENV is inlined, so the branch is dead-code-eliminated).
  useEffect(() => {
    if (!isDevTenantOverrideAllowed()) return
    document.documentElement.dataset.tenant = current
  }, [current])

  const handleClick = () => {
    startTransition(async () => {
      await setDevTenantOverride(next)
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      aria-label={`Switch tenant to ${next}`}
      className="rounded bg-black/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-current hover:bg-black/25 disabled:opacity-50"
    >
      Tenant: {current} → {next}
    </button>
  )
}
